import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { styles } from "@repo/helpers";
import { Button, ButtonTypes, Spinner, ToggleSlider } from "@repo/ui";
import { CircleAlert, GripVertical, Pencil } from "lucide-react";

import type { LayerDefinition } from "../../../models";
import type { LayerResult } from "../hooks/layerQueries";

import { awaitsEnrollment } from "./dateRange";
import { isLayerVisible } from "./layer";
import { RAINBOW } from "./layerAppearance";
import { LayerEditor } from "./layerEditor";
import style from "./style.module.scss";

interface SortableLayerItemProps {
	layer: LayerDefinition;
	result: LayerResult | undefined;
	isEditing: boolean;
	onEdit: () => void;
	onCloseEdit: () => void;
	onToggleVisibility: () => void;
}

export function SortableLayerItem({
	layer,
	result,
	isEditing,
	onEdit,
	onCloseEdit,
	onToggleVisibility,
}: SortableLayerItemProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: layer.id,
	});

	const sortableStyle = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : undefined,
	};

	if (isEditing) {
		return (
			<div ref={setNodeRef} style={sortableStyle}>
				<LayerEditor layerId={layer.id} onClose={onCloseEdit} />
			</div>
		);
	}

	const pinCount = result?.items.length ?? 0;
	const totalCount = result?.totalCount ?? pinCount;
	const countLabel = awaitsEnrollment(layer)
		? "Enter an Enrollment ID"
		: `(${totalCount > pinCount ? `${pinCount} out of ${totalCount}` : pinCount})`;

	return (
		<div ref={setNodeRef} style={sortableStyle} className={style.layerRow}>
			<div className={style.layerRowLeft}>
				<button
					type="button"
					className={style.dragHandle}
					aria-label={`Reorder ${layer.name}`}
					{...attributes}
					{...listeners}
				>
					<GripVertical className={style.dragIcon} />
				</button>
				<span
					className={styles(style.layerColorDot, layer.color === RAINBOW && style.rainbow)}
					style={{ backgroundColor: layer.color }}
				/>
				<div className={style.layerRowInfo}>
					<span className={style.layerRowName}>{layer.name}</span>
					{isLayerVisible(layer) && <span className={style.layerRowCount}>{countLabel}</span>}
					{result?.isLoading && <Spinner size={16} />}
					{result?.isError && (
						<CircleAlert className="h-4 w-4 shrink-0 text-destructive" aria-label="Failed to load" />
					)}
				</div>
			</div>
			<div className={style.layerRowRight}>
				<ToggleSlider
					value={isLayerVisible(layer)}
					label=""
					hideLabel
					changeHandler={onToggleVisibility}
					size="sm"
					className={style.toggleReset}
				/>
				<Button
					types={[ButtonTypes.Text]}
					size="small"
					className={style.editLayerButton}
					onClick={onEdit}
					title="Edit layer"
				>
					<Pencil className={style.editLayerIcon} />
				</Button>
			</div>
		</div>
	);
}
